import { z } from 'zod';

import type {
  CoverReportResponse,
  CoverShipmentHuComparisonSummary,
  CoverShipmentInboundAttachmentReportRow,
  CoverShipmentReportSummary,
  GetCoverShipmentReportRequest,
  GetCoverShipmentReportResponse,
} from './get-cover-shipment-report.dto.js';
import { shipmentFollowUpComparisonStatusValues } from '../shipments/list-scale-tickets.dto.js';
import { ShipmentFormalReportDocumentSchema } from '../shipments/formal-shipping-report-document.schema.js';
import {
  ShipmentReportDailyRowSchema,
  ShipmentReportTotalRowSchema,
} from '../shipments/shipments-shared.schema.js';
import { IsoDateTimeSchema } from '../shipments/shipments-types.js';
import { ReportableWeightLbSchema } from '../reportable-weight.schema.js';

const nonNegativeNumberSchema = z.number().min(0);
const nonNegativeIntSchema = z.number().int().min(0);
const trimmedStringSchema = z.string().trim().min(1);

export const CoverShipmentInboundAttachmentReportRowSchema: z.ZodType<CoverShipmentInboundAttachmentReportRow> = z
  .object({
    receiptId: z.uuid(),
    receiptReference: trimmedStringSchema,
    materialId: z.uuid(),
    receivedAt: IsoDateTimeSchema,
    receivedWeightLb: ReportableWeightLbSchema,
    coverContributionWeightLb: ReportableWeightLbSchema,
    coverContributionRefundValueCents: nonNegativeIntSchema,
    isCrvEligible: z.boolean(),
    redemptionWeightLb: ReportableWeightLbSchema,
    scrapWeightLb: ReportableWeightLbSchema,
  })
  .strict();

export const CoverShipmentReportSummarySchema: z.ZodType<CoverShipmentReportSummary> = z
  .object({
    inboundAttachmentWeightLb: ReportableWeightLbSchema,
    inboundPhysicalReceivedWeightLb: nonNegativeNumberSchema,
    inboundContributionRefundValueCents: nonNegativeIntSchema,
    customerPurchaseWeightLb: ReportableWeightLbSchema,
    totalWeightLb: ReportableWeightLbSchema,
    refundValueCents: nonNegativeIntSchema,
    processorReceivedWeightLb: ReportableWeightLbSchema.nullable(),
    expectedSentLb: nonNegativeNumberSchema,
    minimumAcceptableReceivedLb: nonNegativeNumberSchema,
    differenceFromExpectedSentLb: z.number().nullable(),
    differenceFromExpectedSentPct: z.number().nullable(),
    comparisonStatus: z.enum(shipmentFollowUpComparisonStatusValues),
  })
  .strict();

export const CoverShipmentHuComparisonSummarySchema: z.ZodType<CoverShipmentHuComparisonSummary> = z
  .object({
    sourceLoadSessionId: z.uuid(),
    sourceLoadSessionGroupId: z.uuid(),
    physicalWeightLb: nonNegativeNumberSchema.nullable(),
    differenceFromDocumentWeightLb: z.number().nullable(),
  })
  .strict();

export const GetCoverShipmentReportRequestSchema: z.ZodType<GetCoverShipmentReportRequest> = z
  .object({
    companyId: z.uuid(),
    coverShipmentId: z.uuid(),
  })
  .strict();

export const CoverReportResponseSchema: z.ZodType<CoverReportResponse> = z
  .object({
    coverShipmentId: z.uuid(),
    hasDiscountAdjustedDays: z.boolean(),
    dailyRows: z.array(ShipmentReportDailyRowSchema),
    totalRow: ShipmentReportTotalRowSchema,
    inboundAttachmentRows: z.array(CoverShipmentInboundAttachmentReportRowSchema),
    customerPurchaseRows: z.array(ShipmentReportDailyRowSchema),
    customerPurchaseTotalRow: ShipmentReportTotalRowSchema,
    summary: CoverShipmentReportSummarySchema,
    huComparisonSummary: CoverShipmentHuComparisonSummarySchema.nullable(),
    formalDocument: ShipmentFormalReportDocumentSchema.optional(),
  })
  .strict();

export const GetCoverShipmentReportResponseSchema: z.ZodType<GetCoverShipmentReportResponse> =
  CoverReportResponseSchema;
